import { useState } from 'react'
import { IconCheck, IconChevronDown, IconNote } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { stageApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import type { ProjectStage } from '@/api/types'
import { tr } from '@/i18n/tr'
import {
  Badge, Button, Menu, MenuDivider, MenuItem, MenuLabel, Modal, Textarea, notifications,
} from '@/ui'
import { NAMED_COLOR_LABELS, NAMED_COLORS, ink, solid, tint, type NamedColor } from '@/ui/palette'

/** Durum ve sütunlarda seçilebilen renkler. Sunucu da yalnızca bunları kabul eder. */
export const STATUS_COLORS = Object.keys(NAMED_COLORS) as NamedColor[]

/** Renk seçici: tek seçimli renk kutuları. */
export function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div role="radiogroup" aria-label="Renk" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {STATUS_COLORS.map((c) => {
        const on = value === c
        return (
          <button key={c} type="button" role="radio" aria-checked={on} aria-label={NAMED_COLOR_LABELS[c]} title={NAMED_COLOR_LABELS[c]}
            onClick={() => onChange(c)} className="ui-focusable"
            style={{
              width: 28, height: 28, padding: 0, borderRadius: 8, cursor: 'pointer', background: solid(c), color: '#fff',
              border: `2px solid ${on ? 'var(--nx-fg)' : 'transparent'}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            {on && <IconCheck size={14} stroke={3} />}
          </button>
        )
      })}
    </div>
  )
}

export function StageBadge({ stage, size = 'sm' }: { stage: ProjectStage | null | undefined; size?: 'xs' | 'sm' | 'md' }) {
  const s = size === 'xs' ? 'sm' : 'md'
  if (!stage) return <Badge size={s} tone="neutral">{tr.stage.none}</Badge>
  return <Badge size={s} bg={tint(stage.color)} fg={ink(stage.color)} dot={solid(stage.color)} style={{ fontWeight: 600 }}>{stage.name}</Badge>
}

/**
 * Projenin durumunu gösterir; düzenleme yetkisi olana tıklanınca durum seçimi ve
 * durum notu sunar. Yetkisi olmayana düz rozet görünür.
 */
export function StagePicker({
  projectId, stages, stageId, note, canEdit, size = 'sm',
}: {
  projectId: string
  stages: ProjectStage[]
  stageId: string | null
  note: string | null
  canEdit: boolean
  size?: 'xs' | 'sm' | 'md'
}) {
  const qc = useQueryClient()
  const [noteOpen, setNoteOpen] = useState(false)
  const current = stages.find((s) => s.id === stageId) ?? null

  const save = useMutation({
    mutationFn: (v: { stageId: string | null; note: string | null }) =>
      stageApi.setProgress(projectId, v.stageId, v.note),
    onSuccess: (p) => {
      qc.setQueryData(['progress', projectId], p)
      qc.invalidateQueries({ queryKey: ['project-overview'] })
      qc.invalidateQueries({ queryKey: ['activity', projectId] })
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  if (!canEdit) return <StageBadge stage={current} size={size} />

  const small = size === 'xs'
  return (
    <>
      <Menu width={240} align="start" trigger={
        <button type="button" aria-label={tr.stage.setStage} className="ui-focusable"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, height: small ? 20 : 24, padding: small ? '0 7px' : '0 9px',
            border: 0, borderRadius: 999, cursor: 'pointer', font: 'inherit', fontSize: small ? 11 : 12, fontWeight: 600,
            background: current ? tint(current.color) : 'var(--nx-neutral-bg)', color: current ? ink(current.color) : 'var(--nx-neutral-fg)',
          }}>
          {current && <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 99, background: solid(current.color) }} />}
          {current?.name ?? tr.stage.none}
          <IconChevronDown size={11} stroke={2.4} aria-hidden="true" />
        </button>
      }>
        <MenuLabel>{tr.stage.setStage}</MenuLabel>
        {stages.map((s) => (
          <MenuItem key={s.id}
            icon={<span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: solid(s.color) }} />}
            right={s.id === stageId ? <IconCheck size={14} /> : undefined}
            onSelect={() => s.id !== stageId && save.mutate({ stageId: s.id, note })}>
            {s.name}
          </MenuItem>
        ))}
        <MenuItem
          icon={<span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, border: '1px solid var(--nx-border-strong)' }} />}
          right={stageId === null ? <IconCheck size={14} /> : undefined}
          onSelect={() => stageId !== null && save.mutate({ stageId: null, note })}>
          <span style={{ color: 'var(--nx-fg-muted)' }}>{tr.stage.none}</span>
        </MenuItem>
        <MenuDivider />
        <MenuItem icon={<IconNote size={16} />} onSelect={() => setNoteOpen(true)}>{tr.stage.editNote}</MenuItem>
      </Menu>

      {/* Her açılışta yeniden kurulur: güncel not yüklensin. */}
      {noteOpen && <NoteModal
        initial={note ?? ''}
        saving={save.isPending}
        onClose={() => setNoteOpen(false)}
        onSave={(v) => save.mutate({ stageId, note: v.trim() || null }, { onSuccess: () => setNoteOpen(false) })}
      />}
    </>
  )
}

function NoteModal({
  initial, saving, onClose, onSave,
}: { initial: string; saving: boolean; onClose: () => void; onSave: (v: string) => void }) {
  const [value, setValue] = useState(initial)
  return (
    <Modal opened onClose={onClose} title={tr.stage.note} size="sm"
      footer={<>
        <Button variant="secondary" onClick={onClose}>{tr.common.cancel}</Button>
        <Button loading={saving} onClick={() => onSave(value)}>{tr.common.save}</Button>
      </>}>
      <Textarea aria-label={tr.stage.note} value={value} onChange={(e) => setValue(e.currentTarget.value)}
        placeholder={tr.stage.notePlaceholder} autosize minRows={2} maxLength={500} autoFocus />
    </Modal>
  )
}
